/**
 * Represents a type with a specific property removed.
 * @template T The original object type.
 * @template K The key to be removed from T.
 */
type RemoveProperty<T, K extends keyof T> = {
  [P in Exclude<keyof T, K>]: T[P];
};

/**
 * Removes a specified property from an object and returns a new object without that property.
 *
 * @template T The type of the input object.
 * @template K The literal type of the key to remove, must be a key of T.
 * @param {T} obj - The original object.
 * @param {K} key - The key of the property to remove.
 * @returns {RemoveProperty<T, K>} A new object with the same properties as the input object, except for the specified key.
 *
 * @example
 * const person = { name: "Alice", age: 30, email: "alice@example.com" };
 * const personWithoutEmail = removeProperty(person, "email");
 * // Result: { name: "Alice", age: 30 }
 */
export function removeProperty<T, K extends keyof T>(
  obj: T,
  key: K,
): RemoveProperty<T, K> {
  const { [key]: _, ...rest } = obj;
  return rest;
}
